/**
 * Collection page (also reused as-is by the Search page's .section-search —
 * both share the same filter drawer, sorting, and pagination wiring, see
 * the IS_SEARCH / GRID_SELECTOR / PAGINATION_SELECTOR switches below).
 *
 * Handles:
 * - Filter drawer open / close with focus trap & a11y
 * - AJAX filtering with dynamic "Show x results" button
 * - Sort select
 * - Infinite scroll (auto), load-more button, or classic pagination
 * - URL updates (?page=x, filter params, sort_by)
 */

(function () {
  'use strict';

  var section = document.querySelector('.section-collection') || document.querySelector('.section-search');
  if (!section) return;

  /* Search page reuses this whole script (filter drawer, sorting, pagination
     wiring) instead of a second implementation — it only differs from the
     Collection page in which root/grid/pagination selectors it targets. */
  var IS_SEARCH          = section.classList.contains('section-search');
  var ROOT_SELECTOR      = IS_SEARCH ? '.section-search' : '.section-collection';
  var GRID_SELECTOR      = IS_SEARCH ? '.search-results' : '.collection-grid';
  var PAGINATION_SELECTOR = IS_SEARCH ? '.search-pagination' : '.collection-pagination';

  var SECTION_ID  = section.dataset.sectionId;
  var LOAD_MODE   = section.dataset.loadMore || 'auto_scroll';

  var isLoading     = false;
  var _prevFocused  = null;

  /* ── helpers ───────────────────────────────────────────────── */

  function updateURL(url, replace) {
    if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
  }

  /**
   * Fetch a section using the Sections Rendering API (?sections=).
   * This preserves template-assigned section settings unlike ?section_id=.
   * Returns a parsed Document from the section HTML.
   */
  function fetchSectionDoc(url) {
    var separator = url.indexOf('?') > -1 ? '&' : '?';
    var fetchUrl  = url + separator + 'sections=' + SECTION_ID;

    return fetch(fetchUrl)
      .then(function (res) { return res.json(); })
      .then(function (json) {
        var html = json[SECTION_ID];
        if (!html) throw new Error('Section not found in response');
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  /* ── Section replacement (for filter / sort / popstate) ───── */

  function replaceSection(doc) {
    var newSection = doc.querySelector(ROOT_SELECTOR);
    if (newSection && section.parentNode) {
      section.parentNode.replaceChild(newSection, section);
      section = newSection;

      if (LOAD_MODE === 'auto_scroll') {
        initAutoScroll();
      }

      if (typeof quickAddButton === 'function') {
        quickAddButton();
      }
      if (typeof initProductCardSplides === 'function') {
        initProductCardSplides();
      }
    }
  }

  /* ── Filter drawer ─────────────────────────────────────────── */

  function getFilterDrawer() {
    return section.querySelector('.collection-filter-drawer');
  }

  /**
   * Align the drawer's top edge with the bottom of the header toolbar
   * (the header is fixed, so viewport coordinates map directly to
   * the drawer's fixed positioning). +1px matches the header row gap.
   */
  function positionFilterDrawer(drawer) {
    if (!drawer) return;
    var toolbar = document.querySelector('.header-collection-toolbar');
    if (!toolbar) return;
    var rect = toolbar.getBoundingClientRect();
    drawer.style.setProperty('--filter-drawer-top', (rect.bottom + 1) + 'px');
  }

  /**
   * Keeps both header triggers (Filter button, Sort button) in sync with the
   * drawer's actual open state — both icons rotate together no matter which
   * button opened the drawer, and both return to normal on close.
   */
  function setFilterToggleExpanded(expanded) {
    document.querySelectorAll('.collection-filter-toggle, .js-sort-toggle').forEach(function (btn) {
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  /* Collapse every accordion so each open starts from a clean state
     (the drawer DOM persists between opens) */
  function closeAllFilterGroups() {
    section.querySelectorAll('.collection-filter-group.is-open').forEach(function (group) {
      group.classList.remove('is-open');
      var btn = group.querySelector('.collection-filter-group__title');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * opts.openSorting — pre-open the sorting accordion (sort button trigger).
   * The accordion reset (and optional sorting pre-open) is applied with
   * transitions suppressed and styles flushed, so the drawer is already in
   * its final state on the first visible frame — no open-then-close flicker.
   */
  function openFilterDrawer(opts) {
    var drawer = getFilterDrawer();
    if (!drawer) return;
    _prevFocused = document.activeElement;
    positionFilterDrawer(drawer);

    drawer.classList.add('is-resetting');
    closeAllFilterGroups();
    if (opts && opts.openSorting) {
      var sortGroup = section.querySelector('.collection-filter-group[data-sorting-group]');
      if (sortGroup) {
        sortGroup.classList.add('is-open');
        var sortGroupBtn = sortGroup.querySelector('.collection-filter-group__title');
        if (sortGroupBtn) sortGroupBtn.setAttribute('aria-expanded', 'true');
      }
    }
    void drawer.offsetHeight; /* flush styles while transitions are off */
    drawer.classList.remove('is-resetting');

    /* Close any other header drawer so overlays don't stack */
    if (window.closeOtherDrawers) window.closeOtherDrawers(['open-filter']);
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('open-filter');
    setFilterToggleExpanded(true);
    drawer.focus();
  }

  function closeFilterDrawer() {
    var drawer = getFilterDrawer();
    document.body.classList.remove('open-filter');
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'true');
    }
    setFilterToggleExpanded(false);
    if (_prevFocused && _prevFocused.focus) _prevFocused.focus();
  }

  /* The Filter Drawer's own panel visibility is driven entirely by its
     aria-hidden attribute (assets/collection.css), independent of the
     open-filter body class — so removing the class alone (what the other
     drawers' openers do) doesn't hide it. Registering the real close
     function here is what actually closes it when another drawer opens. */
  if (window.registerDrawerCloser) window.registerDrawerCloser('open-filter', closeFilterDrawer);

  /* ── Availability injection ───────────────────────────────── */

  /**
   * When a size filter is active, automatically add filter.v.availability=1
   * so only in-stock sizes are shown.
   */
  function injectAvailabilityForSize(params) {
    var hasSize = false;
    params.forEach(function (value, key) {
      if (key.toLowerCase().indexOf('size') > -1) {
        hasSize = true;
      }
    });
    if (hasSize) {
      params.set('filter.v.availability', '1');
    }
  }

  /* ── Active filter counts & badges ─────────────────────────── */

  function getFilterForm() {
    return section.querySelector('.collection-filter-form');
  }

  function groupSelectedCount(group) {
    var count = group.querySelectorAll('input[type="checkbox"]:checked').length;

    var priceWrap = group.querySelector('.collection-filter-price');
    if (priceWrap) {
      var priceActive = false;
      priceWrap.querySelectorAll('.collection-filter-price__field input').forEach(function (inp) {
        if (inp.value === '') return;
        var v = parseFloat(inp.value);
        if (isNaN(v)) return;
        var limit = parseFloat(inp.max);
        var isMin = (inp.name || '').indexOf('gte') > -1;
        if (isMin && v > 0) priceActive = true;
        if (!isMin && (isNaN(limit) || v < limit)) priceActive = true;
      });
      if (priceActive) count += 1;
    }

    return count;
  }

  /**
   * Recalculate the per-accordion badges, the header "Filter (n)" badge
   * and the apply-button badge from the drawer's current input state.
   * Sorting is excluded from counts (a sort choice is always present).
   */
  function updateFilterCounts() {
    var form = getFilterForm();
    if (!form) return;

    var total = 0;

    form.querySelectorAll('.collection-filter-group').forEach(function (group) {
      var isSort = group.hasAttribute('data-sorting-group');
      var count = isSort ? 0 : groupSelectedCount(group);
      total += count;

      var badge = group.querySelector('.collection-filter-group__count');
      if (badge) {
        badge.textContent = count;
        badge.style.display = count > 0 ? '' : 'none';
      }
      group.classList.toggle('has-selection', isSort || count > 0);
    });

    var headerBadge = document.querySelector('.collection-filter-header-count');
    if (headerBadge) {
      headerBadge.textContent = total;
      headerBadge.style.display = total > 0 ? '' : 'none';
    }

    var applyBadge = section.querySelector('.collection-filter-apply__count');
    if (applyBadge) {
      applyBadge.textContent = total;
      applyBadge.style.display = total > 0 ? '' : 'none';
    }
  }

  /* ── Price slider ↔ number fields sync ─────────────────────── */

  function updateSliderFill(wrap) {
    var minR = wrap.querySelector('.collection-filter-price__range--min');
    var maxR = wrap.querySelector('.collection-filter-price__range--max');
    if (!minR || !maxR) return;

    var limit = parseFloat(maxR.max) || 100;
    var minV = parseFloat(minR.value) || 0;
    var maxV = parseFloat(maxR.value);
    if (isNaN(maxV)) maxV = limit;

    wrap.style.setProperty('--range-min-pct', Math.max(0, Math.min(100, (minV / limit) * 100)) + '%');
    wrap.style.setProperty('--range-max-pct', Math.max(0, Math.min(100, (maxV / limit) * 100)) + '%');
  }

  function syncPriceFromSlider(range) {
    var wrap = range.closest('.collection-filter-price');
    if (!wrap) return;
    var minR = wrap.querySelector('.collection-filter-price__range--min');
    var maxR = wrap.querySelector('.collection-filter-price__range--max');
    if (!minR || !maxR) return;

    var limit = parseFloat(maxR.max) || 0;
    var minV = parseFloat(minR.value) || 0;
    var maxV = parseFloat(maxR.value);
    if (isNaN(maxV)) maxV = limit;

    /* Keep the two handles ordered */
    if (minV > maxV) {
      if (range === minR) {
        minV = maxV;
        minR.value = minV;
      } else {
        maxV = minV;
        maxR.value = maxV;
      }
    }

    /* Empty fields at the extremes mean "no price filter" */
    wrap.querySelectorAll('.collection-filter-price__field input').forEach(function (inp) {
      var isMin = (inp.name || '').indexOf('gte') > -1;
      if (isMin) {
        inp.value = minV > 0 ? minV : '';
      } else {
        inp.value = maxV < limit ? maxV : '';
      }
    });

    updateSliderFill(wrap);
  }

  function syncPriceFromInput(input) {
    var wrap = input.closest('.collection-filter-price');
    if (!wrap) return;
    var minR = wrap.querySelector('.collection-filter-price__range--min');
    var maxR = wrap.querySelector('.collection-filter-price__range--max');
    if (!minR || !maxR) return;

    var limit = parseFloat(maxR.max) || 0;
    var isMin = (input.name || '').indexOf('gte') > -1;
    var v = parseFloat(input.value);

    if (isMin) {
      minR.value = isNaN(v) ? 0 : v;
    } else {
      maxR.value = isNaN(v) ? limit : v;
    }

    updateSliderFill(wrap);
  }

  /* ── Auto-apply: refresh products on any filter change ─────── */

  function buildFilterParams(form, opts) {
    var formData = new FormData(form);
    var params   = new URLSearchParams();

    for (var pair of formData.entries()) {
      if (pair[1]) params.append(pair[0], pair[1]);
    }

    if (opts && opts.skipSort) {
      /* Clearing: drop sort_by entirely instead of trying to match it to a
         "default" value, so Shopify applies the collection's real default
         order the same way a plain link to the collection URL would. */
      params.delete('sort_by');
    } else {
      /* Sorting: the drawer's sort radios take priority; keep the header
         sort button label / hidden select in sync */
      var sortSelect = document.querySelector('.collection-sort-select');
      var formSort   = params.get('sort_by');

      if (formSort) {
        if (sortSelect && sortSelect.value !== formSort) {
          sortSelect.value = formSort;
        }
        if (sortSelect && sortSelect.selectedIndex > -1) {
          var sortLabel = document.querySelector('.js-sort-current-label');
          if (sortLabel) sortLabel.textContent = sortSelect.options[sortSelect.selectedIndex].text;
        }
      } else if (sortSelect && sortSelect.value) {
        params.set('sort_by', sortSelect.value);
      }
    }

    injectAvailabilityForSize(params);

    return params;
  }

  /**
   * Swap products in place (grid, pagination, active-filter tags) WITHOUT
   * re-rendering the drawer, so open accordions, slider positions and
   * focus inside the drawer are preserved while the user keeps filtering.
   */
  function updateProductsFromDoc(doc) {
    var newSection = doc.querySelector(ROOT_SELECTOR);
    if (!newSection) return;

    var grid    = section.querySelector(GRID_SELECTOR);
    var newGrid = newSection.querySelector(GRID_SELECTOR);
    if (grid && newGrid) grid.replaceWith(newGrid);

    var pagination    = section.querySelector(PAGINATION_SELECTOR);
    var newPagination = newSection.querySelector(PAGINATION_SELECTOR);
    if (pagination && newPagination) pagination.replaceWith(newPagination);

    var tags    = section.querySelector('.collection-active-filters');
    var newTags = newSection.querySelector('.collection-active-filters');
    if (tags && newTags) {
      tags.replaceWith(newTags);
    } else if (tags && !newTags) {
      tags.remove();
    } else if (!tags && newTags) {
      var anchor = section.querySelector(GRID_SELECTOR);
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(newTags, anchor);
    }

    section.dataset.totalPages    = newSection.dataset.totalPages || '1';
    section.dataset.currentPage   = newSection.dataset.currentPage || '1';
    section.dataset.productsCount = newSection.dataset.productsCount || '0';

    if (LOAD_MODE === 'auto_scroll') {
      initAutoScroll();
    }
    if (typeof quickAddButton === 'function') {
      quickAddButton();
    }
    if (typeof initProductCardSplides === 'function') {
      initProductCardSplides();
    }
  }

  var _applyTimer = null;

  function applyFiltersNow(opts) {
    clearTimeout(_applyTimer);
    var form = getFilterForm();
    if (!form) return;

    var params    = buildFilterParams(form, opts);
    var paramsStr = params.toString();
    var newUrl    = paramsStr ? form.dataset.collectionUrl + '?' + paramsStr : form.dataset.collectionUrl;

    updateURL(newUrl);

    fetchSectionDoc(newUrl)
      .then(updateProductsFromDoc)
      .catch(console.error);
  }

  function scheduleAutoApply(delay) {
    clearTimeout(_applyTimer);
    _applyTimer = setTimeout(applyFiltersNow, delay || 0);
  }

  /* ── Filter form submit (Apply button) ─────────────────────── */

  /**
   * Products already refresh automatically on every filter change, so the
   * apply button just flushes any pending change and closes the drawer.
   */
  function handleFilterSubmit(e) {
    e.preventDefault();
    applyFiltersNow();
    closeFilterDrawer();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ── Sort ──────────────────────────────────────────────────── */

  function handleSort(e) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');

    updateURL(url.toString());

    fetchSectionDoc(url.toString())
      .then(function (doc) {
        replaceSection(doc);
      })
      .catch(console.error);
  }

  /* ── Load more / infinite scroll ───────────────────────────── */

  function loadNextPage(loadMoreEl) {
    if (isLoading || !loadMoreEl) return;

    var nextUrl = loadMoreEl.getAttribute('href');
    if (!nextUrl) return;

    isLoading = true;
    loadMoreEl.classList.add('is-loading');

    fetchSectionDoc(nextUrl)
      .then(function (doc) {
        var productGrid = section.querySelector('.collection-grid');
        var newGrid     = doc.querySelector('.collection-grid');

        /* Append new products to the live grid */
        if (newGrid && productGrid) {
          var items = newGrid.querySelectorAll('.product-item, .collection-promo-card');
          for (var i = 0; i < items.length; i++) {
            productGrid.appendChild(items[i]);
          }
        }

        /* Check if there is another page after the one we just loaded */
        var nextLink = doc.querySelector('[data-load-more]');
        var hasMore  = false;

        if (nextLink && nextLink.getAttribute('href')) {
          loadMoreEl.setAttribute('href', nextLink.getAttribute('href'));
          hasMore = true;
        } else {
          /* Fallback: use section data attributes to determine next page */
          var newSection = doc.querySelector('.section-collection');
          if (newSection) {
            var totalPages  = parseInt(newSection.dataset.totalPages) || 0;
            var currentPage = parseInt(newSection.dataset.currentPage) || 0;
            if (currentPage < totalPages) {
              var nextPageUrl = new URL(nextUrl, window.location.origin);
              nextPageUrl.searchParams.set('page', currentPage + 1);
              loadMoreEl.setAttribute('href', nextPageUrl.pathname + nextPageUrl.search);
              hasMore = true;
            }
          }
        }

        if (hasMore) {
          loadMoreEl.style.display = '';
        } else {
          /* No more pages — hide the trigger */
          loadMoreEl.style.display = 'none';
          if (autoScrollObserver) autoScrollObserver.disconnect();
        }

        /* Update "Page X of Y" text (load_more_btn mode) */
        var liveInfo = section.querySelector('.collection-pagination-info');
        var newInfo  = doc.querySelector('.collection-pagination-info');
        if (liveInfo && newInfo) {
          liveInfo.innerHTML = newInfo.innerHTML;
        } else if (liveInfo && !hasMore) {
          /* Last page reached — the fetched doc won't contain pagination-info
             because paginate.next is falsy, so update manually */
          var totalPages = section.dataset.totalPages || '?';
          liveInfo.textContent = liveInfo.textContent.replace(/\d+(\s*(?:of|\/)\s*)\d+/, totalPages + '$1' + totalPages);
        }

        /* Update URL with loaded page */
        try {
          var urlObj    = new URL(nextUrl, window.location.origin);
          var page      = urlObj.searchParams.get('page');
          if (page) {
            var currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('page', page);
            updateURL(currentUrl.toString(), true);
            section.dataset.currentPage = page;
          }
        } catch (e) { /* ignore URL parse errors */ }

        /* Re-bind quick add */
        if (typeof quickAddButton === 'function') {
          quickAddButton();
        }
      })
      .catch(function (err) {
        console.error('Load more error:', err);
      })
      .finally(function () {
        isLoading = false;
        loadMoreEl.classList.remove('is-loading');
      });
  }

  /* ── Auto scroll observer ──────────────────────────────────── */

  var autoScrollObserver = null;

  function initAutoScroll() {
    if (autoScrollObserver) autoScrollObserver.disconnect();

    var loadMoreEl = section.querySelector('.collection-load-more--auto[data-load-more]');
    if (!loadMoreEl) return;

    autoScrollObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !isLoading) {
          loadNextPage(loadMoreEl);
        }
      });
    }, { rootMargin: '200px' });

    autoScrollObserver.observe(loadMoreEl);
  }

  /* ── Delegated event listeners ─────────────────────────────── */

  /* Overlay click closes filter (capture phase to bypass cart-drawer stopPropagation) */
  var overlay = document.querySelector('.overlay-box');
  if (overlay) {
    overlay.addEventListener('click', function () {
      if (document.body.classList.contains('open-filter')) {
        closeFilterDrawer();
      }
    }, true);
  }

  document.addEventListener('click', function (e) {
    /* Toggle filter drawer */
    if (e.target.closest('.collection-filter-toggle')) {
      if (document.body.classList.contains('open-filter')) {
        closeFilterDrawer();
      } else {
        openFilterDrawer();
      }
      return;
    }

    /* Close filter drawer */
    if (e.target.closest('.collection-filter-close')) {
      closeFilterDrawer();
      return;
    }

    /* Accordion toggle inside filter drawer */
    var groupBtn = e.target.closest('.collection-filter-group__title');
    if (groupBtn) {
      var group = groupBtn.closest('.collection-filter-group');
      if (group) {
        var nowOpen = group.classList.toggle('is-open');
        groupBtn.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
      }
      return;
    }

    /* Sort toolbar button opens the drawer with the sorting accordion expanded */
    var sortToggle = e.target.closest('.js-sort-toggle');
    if (sortToggle && getFilterDrawer()) {
      e.preventDefault();
      if (document.body.classList.contains('open-filter')) {
        closeFilterDrawer();
      } else {
        openFilterDrawer({ openSorting: true });
      }
      return;
    }

    /* Clear all filters and sorting in place (no page reload) */
    if (e.target.closest('.collection-filter-clear')) {
      e.preventDefault();
      var form = getFilterForm();
      if (!form) return;

      form.querySelectorAll('input[type="checkbox"]:checked').forEach(function (cb) {
        cb.checked = false;
      });
      form.querySelectorAll('.collection-filter-price').forEach(function (wrap) {
        var minR = wrap.querySelector('.collection-filter-price__range--min');
        var maxR = wrap.querySelector('.collection-filter-price__range--max');
        if (minR) minR.value = minR.min || 0;
        if (maxR) maxR.value = maxR.max || 0;
        wrap.querySelectorAll('.collection-filter-price__field input').forEach(function (inp) {
          inp.value = '';
        });
        updateSliderFill(wrap);
      });

      /* Reset the sort UI (drawer radios, header select/label) to the
         collection default. The URL itself drops sort_by entirely (via
         applyFiltersNow's skipSort below) rather than relying on this value
         exactly matching a radio/option — Shopify then applies the
         collection's real default order regardless of naming. */
      var defaultSort = form.dataset.defaultSort;
      if (defaultSort) {
        form.querySelectorAll('input[name="sort_by"]').forEach(function (radio) {
          radio.checked = (radio.value === defaultSort);
        });

        var sortSelect = document.querySelector('.collection-sort-select');
        if (sortSelect) {
          sortSelect.value = defaultSort;
          var sortLabel = document.querySelector('.js-sort-current-label');
          if (sortLabel && sortSelect.selectedIndex > -1) {
            sortLabel.textContent = sortSelect.options[sortSelect.selectedIndex].text;
          }
        }
      }

      updateFilterCounts();
      applyFiltersNow({ skipSort: true });
      return;
    }

    /* Click on drawer backdrop (outside inner panel) */
    var drawer = getFilterDrawer();
    if (drawer && document.body.classList.contains('open-filter') && e.target === drawer) {
      closeFilterDrawer();
      return;
    }

    /* Load more button */
    var loadMoreBtn = e.target.closest('.collection-load-more--btn[data-load-more]');
    if (loadMoreBtn) {
      e.preventDefault();
      loadNextPage(loadMoreBtn);
      return;
    }
  });

  /* Escape key closes filter */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.body.classList.contains('open-filter')) {
      closeFilterDrawer();
      return;
    }

    /* Focus trap inside filter drawer */
    if (e.key === 'Tab' && document.body.classList.contains('open-filter')) {
      var drawer = getFilterDrawer();
      if (!drawer) return;

      var focusable = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), details > summary'
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  });

  /* Sort change (header hidden select) */
  document.addEventListener('change', function (e) {
    if (e.target.closest('.collection-sort-select')) {
      handleSort(e);
      return;
    }

    /* Any filter change: refresh badges and auto-apply products */
    if (e.target.closest('.collection-filter-form')) {
      updateFilterCounts();
      scheduleAutoApply(0);
    }
  });

  /* Price slider handles & number fields (live while dragging/typing) */
  document.addEventListener('input', function (e) {
    var range = e.target.closest('.collection-filter-price__range');
    if (range) {
      syncPriceFromSlider(range);
      updateFilterCounts();
      scheduleAutoApply(400);
      return;
    }

    if (e.target.closest('.collection-filter-price__field input')) {
      syncPriceFromInput(e.target);
      updateFilterCounts();
      scheduleAutoApply(500);
    }
  });

  /* Filter form submit */
  document.addEventListener('submit', function (e) {
    if (e.target.closest('.collection-filter-form')) {
      handleFilterSubmit(e);
    }
  });

  /* Keep drawer aligned with the toolbar while open */
  window.addEventListener('resize', function () {
    if (document.body.classList.contains('open-filter')) {
      positionFilterDrawer(getFilterDrawer());
    }
  });

  /* Browser back/forward */
  window.addEventListener('popstate', function () {
    fetchSectionDoc(window.location.href)
      .then(function (doc) {
        replaceSection(doc);
      })
      .catch(console.error);
  });

  /* ── Load previous pages on refresh (infinite scroll / load-more) ── */

  /**
   * When the page loads directly at ?page=N (e.g. refresh, shared link, or
   * back-navigation after scrolling), Shopify only renders page N's slice.
   * Fetch pages 1…N-1 and prepend them so the grid is complete, preserving
   * the user's scroll position.
   */
  function loadPreviousPages() {
    var currentPage = parseInt(section.dataset.currentPage) || 1;
    if (currentPage <= 1) return;

    var productGrid = section.querySelector('.collection-grid');
    if (!productGrid) return;

    var baseUrl  = new URL(window.location.href);
    var promises = [];

    for (var p = 1; p < currentPage; p++) {
      var pageUrl = new URL(baseUrl.toString());
      pageUrl.searchParams.set('page', String(p));
      promises.push(fetchSectionDoc(pageUrl.toString()));
    }

    section.classList.add('is-loading-previous');

    Promise.all(promises)
      .then(function (docs) {
        var fragment = document.createDocumentFragment();

        for (var i = 0; i < docs.length; i++) {
          var newGrid = docs[i].querySelector('.collection-grid');
          if (!newGrid) continue;
          var items = newGrid.querySelectorAll('.product-item, .collection-promo-card');
          for (var j = 0; j < items.length; j++) {
            fragment.appendChild(items[j]);
          }
        }

        /* Preserve scroll position while prepending */
        var scrollBefore = window.pageYOffset;
        var heightBefore = document.documentElement.scrollHeight;

        productGrid.insertBefore(fragment, productGrid.firstChild);

        requestAnimationFrame(function () {
          var heightAfter = document.documentElement.scrollHeight;
          window.scrollTo(0, scrollBefore + (heightAfter - heightBefore));
        });

        section.classList.remove('is-loading-previous');

        if (typeof quickAddButton === 'function') quickAddButton();
      })
      .catch(function (err) {
        console.error('Error loading previous pages:', err);
        section.classList.remove('is-loading-previous');
      });
  }

  /* ── Quick Nav Splide Slider ──────────────────────────────── */

  function initCollectionQuickNavSplides() {
    if (typeof Splide === 'undefined') return;

    var elements = document.querySelectorAll('.js-collection-quick-nav-splide');
    elements.forEach(function (el) {
      if (el.dataset.quickNavInit) return;

      var count = el.querySelectorAll('.collection-quick-nav__item').length;

      var slider = new Splide(el, {
        type: 'slide',
        perPage: Math.min(count, 6),
        perMove: 1,
        gap: '16px',
        pagination: false,
        arrows: false,
        drag: false,
        destroy: true,
        breakpoints: {
          991: {
            destroy: false,
            perPage: 4,
            perMove: 1,
            gap: '10px',
            drag: true,
            arrows: false
          }
        }
      });

      var prevBtn = el.querySelector('.collection-quick-nav__arrows .collection-quick-nav__arrow-btn--prev');
      var nextBtn = el.querySelector('.collection-quick-nav__arrows .collection-quick-nav__arrow-btn--next');

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          slider.go('<');
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          slider.go('>');
        });
      }

      var bar = el.querySelector('.js-collection-quick-nav-progress-bar');

      function syncControls() {
        var controller = slider.Components && slider.Components.Controller;
        if (!controller) return;

        var end = controller.getEnd();
        if (!isFinite(end) || end < 0) end = 0;

        var index = Math.min(Math.max(slider.index, 0), end);

        if (bar) {
          var rate = Math.min((index + 1) / (end + 1), 1);
          bar.style.width = String(100 * rate) + '%';
        }
        if (prevBtn) {
          prevBtn.disabled = index <= 0;
        }
        if (nextBtn) {
          nextBtn.disabled = index >= end;
        }
      }

      slider.on('mounted move moved updated refresh resized scrolled', syncControls);
      slider.mount();
      el.dataset.quickNavInit = 'true';
    });
  }

  /* ── Init ──────────────────────────────────────────────────── */

  initCollectionQuickNavSplides();
  updateFilterCounts();

  if (LOAD_MODE === 'auto_scroll') {
    initAutoScroll();
  }

  if (LOAD_MODE !== 'pagination') {
    loadPreviousPages();
  }

})();
